import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface OscillatingDotsLoaderProps {
  width?: number;
  height?: number;
}

export default function OscillatingDotsLoader({ width, height = 100 }: OscillatingDotsLoaderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const containerWidth = width || canvas.offsetWidth || 300;
    canvas.width = containerWidth;
    canvas.height = height;

    let time = 0;
    let lastTime = 0;

    // Get primary color from theme
    const primaryColor = theme.palette.primary.main; // #012169
    
    // Convert hex to RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 1, g: 33, b: 105 }; // fallback to primary color
    };

    const primaryRgb = hexToRgb(primaryColor);

    // Oscillation parameters
    const dotCount = Math.floor(containerWidth / 12); // Responsive dot count
    const rowCount = 4;
    const spacing = containerWidth / (dotCount + 1);
    const maxAmplitude = 8 + (rowCount - 1) * 2; // Maximum oscillation amplitude
    const padding = maxAmplitude + 5; // Extra padding for dot radius
    const availableHeight = height - (padding * 2);
    const rowSpacing = availableHeight / (rowCount - 1);

    const animate = (timestamp: number) => {
      if (!lastTime) lastTime = timestamp;
      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;
      time += deltaTime * 0.001;

      ctx.clearRect(0, 0, containerWidth, height);

      // Draw oscillating dots in rows
      for (let row = 0; row < rowCount; row++) {
        const y = padding + rowSpacing * row;
        
        for (let i = 0; i < dotCount; i++) {
          const x = spacing * (i + 1);
          
          // Wave parameters vary by row
          const amplitude = 8 + row * 2;
          const frequency = 1 + row * 0.2;
          const phaseOffset = row * 0.5;
          
          // Calculate vertical offset
          const offset = Math.sin(time * frequency + i * 0.2 + phaseOffset) * amplitude;
          const finalY = y + offset;
          
          // Color intensity based on position and time
          const intensity = 0.6 + Math.sin(time * 2 + i * 0.1 + row * 0.3) * 0.4;
          
          // Draw dot with primary color
          ctx.beginPath();
          ctx.arc(x, finalY, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, ${intensity})`;
          ctx.fill();
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, theme.palette.primary.main]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: width ? `${width}px` : '100%',
          height: `${height}px`,
          maxWidth: '100%'
        }}
      />
    </Box>
  );
}